import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useUploadNoteAttachment, useDeleteNoteAttachment } from '../hooks/useNoteRequests';
import { prepareImage, formatBytes, MAX_ATTACHMENTS } from '../utils/imagePrep';
import { formatDateTimeEastern } from '../utils/dates';
import { pluralize } from './NoteRequestBits';

// Photos on note requests (/orders page + Apply modal).
//   useAttachmentDraft + AttachmentPicker : photos chosen on the submit form
//       before the request exists — prepared client-side, uploaded after create
//   AttachmentStrip : thumbnails on a request row; "+ Photo" uploads directly
//   Lightbox        : full view, Open full size, Delete when allowed
//   AttachmentBadge : paperclip + count chip on collapsed rows
// Images are either data: URLs (draft previews) or the same-origin /api URL
// from the request view (cookie auth) — the CSP blocks blob: URLs.

// User-facing text for a failed upload. nginx answers an oversized body with
// an HTML 413, so any 413 is "too large" whatever the body says.
export function uploadErrorText(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  if (status === 413 || code === 'TOO_LARGE') return 'Image is too large';
  if (code === 'TOO_MANY') return `Only ${MAX_ATTACHMENTS} photos per request`;
  if (status === 415 || code === 'BAD_TYPE') return 'Use a PNG or JPG screenshot';
  if (code === 'EMPTY') return 'The image was empty';
  if (status === 403) return "You can't add photos to this request";
  if (status === 404) return 'Request not found';
  if (status === 429) return 'Too many uploads — wait a minute and try again';
  if (err?.code === 'ECONNABORTED') return 'Upload timed out';
  return err?.response?.data?.error || err?.message || 'Upload failed';
}

// 403/404/409 mean every remaining upload to this request fails the same way
const isFatalUpload = (err) => [403, 404, 409].includes(err?.response?.status);

// Uploads prepared { blob, name } items one at a time; never throws.
// → { ok, failed, forbidden (first 403), reason }
export async function uploadAll(uploadAsync, id, items) {
  let ok = 0;
  let failed = 0;
  let forbidden = false;
  const reasons = [];
  for (let i = 0; i < items.length; i++) {
    try {
      await uploadAsync({ id, blob: items[i].blob, name: items[i].name });
      ok += 1;
    } catch (err) {
      const reason = uploadErrorText(err);
      if (!reasons.includes(reason)) reasons.push(reason);
      if (isFatalUpload(err)) {
        failed += items.length - i;
        forbidden = err.response.status === 403;
        break;
      }
      failed += 1;
    }
  }
  return { ok, failed, forbidden, reason: reasons.join('; ') };
}

let draftSeq = 0;

// Photos picked on the submit form. One queue shared by the picker (button /
// drop) and the form's paste handler. Files are prepared one at a time so a
// handful of 12 MP phone photos never decode at once.
export function useAttachmentDraft(max = MAX_ATTACHMENTS) {
  const [items, setItems] = useState([]);
  const [preparing, setPreparing] = useState(0);
  const [errors, setErrors] = useState([]);
  const count = useRef(0);   // items + files still preparing (bumped before state lands)
  const gen = useRef(0);     // reset() bumps it so late results are dropped
  count.current = items.length + preparing;

  const addFiles = useCallback(async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const take = list.slice(0, Math.max(0, max - count.current));
    setErrors(list.length > take.length
      ? [`Up to ${max} photos per request — ${pluralize(list.length - take.length, 'file')} skipped`]
      : []);
    if (!take.length) return;
    const mine = gen.current;
    count.current += take.length;
    setPreparing(n => n + take.length);
    for (const file of take) {
      let item = null;
      let error = null;
      try {
        const out = await prepareImage(file);
        draftSeq += 1;
        item = { key: draftSeq, ...out, size: out.blob.size };
      } catch (e) {
        error = `${file.name || 'Image'}: ${e.message || 'could not be read'}`;
      }
      if (gen.current !== mine) return;
      setPreparing(n => Math.max(0, n - 1));
      if (item) setItems(prev => [...prev, item]);
      else setErrors(prev => [...prev, error]);
    }
  }, [max]);

  const remove = useCallback((key) => setItems(prev => prev.filter(i => i.key !== key)), []);

  const reset = useCallback(() => {
    gen.current += 1;
    setItems([]);
    setPreparing(0);
    setErrors([]);
  }, []);

  return { items, preparing, errors, max, addFiles, remove, reset };
}

// Submit-form picker: Add photo button, drop zone (focusable so Ctrl+V works
// after clicking it), thumbnails with ×, per-file prep errors.
export function AttachmentPicker({ draft, disabled }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const { items, preparing, errors, max, addFiles, remove } = draft;
  const full = items.length + preparing >= max;

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // the same file can be chosen again after removing it
    addFiles(files);
  };
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  // Always swallow file drags here — a missed drop would navigate away from the form
  const onDragOver = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
    if (!over && !disabled) setOver(true);
  };
  const onDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); };
  const onDrop = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    setOver(false);
    if (!disabled) addFiles(Array.from(e.dataTransfer.files || []));
  };

  return (
    <div className="nr-attach-picker">
      <div
        className={`nr-attach-drop${over ? ' over' : ''}`}
        tabIndex={0}
        aria-label="Photos: paste a screenshot, drop an image, or choose a file"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {items.map(it => (
          <div key={it.key} className="nr-attach-item" title={it.name}>
            <img src={it.previewDataUrl} alt={it.name} />
            <button
              type="button"
              className="nr-attach-remove"
              onClick={() => remove(it.key)}
              disabled={disabled}
              aria-label={`Remove ${it.name}`}
            >
              ×
            </button>
            <div className="nr-attach-size">{formatBytes(it.size)}</div>
          </div>
        ))}
        {Array.from({ length: preparing }).map((_, i) => (
          <div key={`prep-${i}`} className="nr-attach-item">
            <div className="nr-attach-pending">preparing…</div>
          </div>
        ))}
        <div className="nr-attach-cta">
          {!full && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => inputRef.current?.click()} disabled={disabled}>
              Add photo
            </button>
          )}
          <span className="nr-attach-hint">
            {full ? `${max} photos max` : 'Paste a screenshot (Ctrl+V), drop it here, or choose a file'}
          </span>
        </div>
        <input ref={inputRef} type="file" accept="image/*" multiple className="nr-attach-input" tabIndex={-1} onChange={onPick} />
      </div>
      {errors.map((msg, i) => <div key={i} className="nr-attach-err">{msg}</div>)}
    </div>
  );
}

// Thumbnails for a saved request. canAdd shows "+ Photo" (prepare → upload);
// canDeleteAtt(att) puts Delete in the lightbox. The server is the authority
// on both (403 → error shown). Clicks don't bubble to expandable rows.
export function AttachmentStrip({ request, canAdd = false, canDeleteAtt }) {
  const atts = request?.attachments || [];
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [delError, setDelError] = useState('');
  const inputRef = useRef(null);
  const upload = useUploadNoteAttachment();
  const del = useDeleteNoteAttachment();
  // By id, so a refetch refreshes it and a delete closes the lightbox
  const open = atts.find(a => a.id === openId) || null;

  if (!atts.length && !canAdd) return null;

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !request?.id) return;
    const room = Math.max(0, MAX_ATTACHMENTS - atts.length);
    const errs = files.length > room ? [`Only ${MAX_ATTACHMENTS} photos per request`] : [];
    setError('');
    setBusy(true);
    try {
      for (const file of files.slice(0, room)) {
        let prepared;
        try {
          prepared = await prepareImage(file);
        } catch (err) {
          errs.push(`${file.name || 'Image'}: ${err.message}`);
          continue;
        }
        try {
          await upload.mutateAsync({ id: request.id, blob: prepared.blob, name: prepared.name });
        } catch (err) {
          errs.push(uploadErrorText(err));
          if (isFatalUpload(err)) break;
        }
      }
    } finally {
      setBusy(false);
      setError(errs.join(' · '));
    }
  };

  const closeLightbox = () => { setOpenId(null); setDelError(''); };

  const doDelete = (att) => {
    if (!window.confirm(`Delete photo “${att.name || 'photo'}” from request #${request.id}?`)) return;
    setDelError('');
    del.mutate({ id: request.id, attId: att.id }, {
      onSuccess: closeLightbox,
      onError: (err) => setDelError(
        err.response?.status === 403 ? 'Only the person who added this photo or an admin can delete it'
          : err.response?.data?.error || 'Failed to delete the photo'
      ),
    });
  };

  return (
    <div className="nr-attach-strip" onClick={(e) => e.stopPropagation()}>
      {atts.map(att => (
        <button
          key={att.id}
          type="button"
          className="nr-attach-thumb"
          onClick={() => { setDelError(''); setOpenId(att.id); }}
          title={[att.name, att.uploadedByName].filter(Boolean).join(' · ') || 'Photo'}
        >
          <img src={att.url} loading="lazy" alt={att.name || 'Attachment'} />
        </button>
      ))}
      {canAdd && atts.length < MAX_ATTACHMENTS && (
        <>
          <button
            type="button"
            className="btn btn-link btn-sm nr-attach-add"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            title="Attach a screenshot or photo to this request"
          >
            {busy ? 'Uploading…' : '+ Photo'}
          </button>
          <input ref={inputRef} type="file" accept="image/*" multiple className="nr-attach-input" tabIndex={-1} onChange={onPick} />
        </>
      )}
      {error && <span className="nr-attach-err">{error}</span>}
      {open && (
        <Lightbox
          attachment={open}
          onClose={closeLightbox}
          onDelete={canDeleteAtt && canDeleteAtt(open) ? () => doDelete(open) : undefined}
          deleting={del.isPending}
          error={delError}
        />
      )}
    </div>
  );
}

// Full-size view over everything (portal; z-index above the Apply modal).
// Esc or a click outside the image closes it.
export function Lightbox({ attachment, onClose, onDelete, deleting = false, error = '' }) {
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey, true);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  if (!attachment) return null;
  const meta = [
    attachment.name,
    attachment.sizeBytes ? formatBytes(attachment.sizeBytes) : null,
    attachment.uploadedByName,
    attachment.createdAt ? `${formatDateTimeEastern(attachment.createdAt)} ET` : null,
  ].filter(Boolean).join(' · ');

  return createPortal(
    <div
      className="nr-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={attachment.name || 'Photo'}
      onClick={(e) => { e.stopPropagation(); onClose?.(); }}
    >
      <div className="nr-lightbox-body" onClick={(e) => e.stopPropagation()}>
        <img src={attachment.url} alt={attachment.name || 'Attachment'} />
        <div className="nr-lightbox-bar">
          <span className="nr-lightbox-meta">{meta}</span>
          <a className="btn btn-secondary btn-sm" href={attachment.url} target="_blank" rel="noreferrer">Open full size</a>
          {onDelete && (
            <button type="button" className="btn btn-danger btn-sm" onClick={onDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button ref={closeRef} type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
        {error && <div className="nr-attach-err">{error}</div>}
      </div>
    </div>,
    document.body
  );
}

export function AttachmentBadge({ count }) {
  if (!count) return null;
  return (
    <span className="nr-chip nr-attach-badge" title={`${pluralize(count, 'photo')} attached`}>
      {'\u{1F4CE}'} {count}
    </span>
  );
}
